import 'ag-grid-community';
import { ColDef, GridApi } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AscIcon from '../../../icons/grid-asc-icon.svg';
import DescIcon from '../../../icons/grid-desc-icon.svg';
import CustomLoadingOverlay from './customLoadingOverlay';
import NoRowsOverlay from './noRowsOverlay';
import { CustomFilterCategory } from '../filters/filter.model';
import * as selector from '../../../selectors/selectors'
import { useSelector } from 'react-redux';

interface Props {
    ColumnDefs: ColDef[];
    RowData: any[];
    OnGridReady: (params: any) => void;
    OnGridRowClick?: (params: any) => void;
    OnSelectionChanged?: () => void;
    IsPaginationEnabled: boolean;
    EmptyLabel: string | React.ReactNode;
    LockVisible: boolean;
    filterOptions?: CustomFilterCategory[];
    IsFilterPresent?: boolean;
    IsSpinerLoader?: boolean;
    gridApi?: GridApi;
    isHorizontalScroll?: boolean;
    IsSettingsGrid?: boolean;
    OnGridCellRowClick?: (e: any, data: any) => void;
    ShowRowPointer?: boolean;
    onColumnResized?: (params: any) => void;
    GetRowId?: (params: { data: any }) => string;
    EnableCellChangeFlash?: boolean;
    RowHeight?: number;
}

const GridComponent = (props: Props) => {
    const isDarkMode: boolean = useSelector(selector.isDarkMode)
    const IsMobileDevice: boolean = useSelector(selector.isMobileDevice)
    const [rowData, setRowData] = useState([]);
    const defaultColDef: ColDef = useMemo(() => {
        return {
            flex: 1,
            sortable: true,
            resizable: true,
            sortingOrder: ['desc', 'asc'],
            cellStyle: (params) => ({
                display: "flex",
                alignItems: "center",
                padding: "0.5rem 1rem",
            }),
            headerClass: "py-1 px-2",
            icons: {
                sortAscending: `<img src=${AscIcon} />`,
                sortDescending: `<img src=${DescIcon} />`,
            },
            enableCellChangeFlash: props.EnableCellChangeFlash ?? true,
            headerCheckboxSelectionFilteredOnly: true,
            lockVisible: props.LockVisible,
            cellClass: props.ShowRowPointer ? "c-pointer" : ""
        }
    }, []);

    useEffect(() => {
        setRowData(props.RowData);
    }, [props.RowData])

    useEffect(() => {
        !!props.gridApi && props.gridApi.onFilterChanged()
    }, [props.filterOptions])

    const noRowsOverlayComponent = useMemo<any>(() => {
        return NoRowsOverlay;
    }, []);

    const noRowsOverlayComponentParams = useMemo<any>(() => {
        return {
            EmptyLabel: props.EmptyLabel
        };
    }, [props.EmptyLabel]);

    const loadingOverlayComponent = useMemo<any>(() => {
        return CustomLoadingOverlay;
    }, []);

    const gridOptions = {
        alwaysShowHorizontalScroll: true,
        suppressMovableColumns: IsMobileDevice ? true : false,
        suppressHorizontalScroll: !props.isHorizontalScroll,
        rowHeight: props?.RowHeight ?? 50
    };

    const isExternalFilterPresent = () => {
        return props.IsFilterPresent
    };

    const doesExternalFilterPass = useCallback((node) => {
        const { data: gridObject } = node;

        const selectedOptions = props.filterOptions.flatMap(x => x.Options.filter(y => y.IsSelected))

        const filterObj: any = selectedOptions.reduce((acc, e) => {
            if (!acc[e.Key]) {
                acc[e.Key] = [e.Value];
            } else {
                acc[e.Key].push(e.Value);
            }
            return acc;
        }, {});

        if (!Object.keys(filterObj).length) {
            return true;
        }
        else {
            return Object.entries(filterObj).every((e: any[]) => {
                let [key, value] = e;
                return value.includes(gridObject[key])
            })
        }

    }, [props.filterOptions])

    return (
        <div className={`${isDarkMode ? "ag-theme-alpine-dark" : "ag-theme-alpine"} h-p-100 w-fill m-auto ${IsMobileDevice ? "pt-1 pb-2" : `${props.IsSettingsGrid ? "settings-paper-border br-4" : " bg-color-mui-secondary p-2"}`}`} data-ag-theme-mode={'dark'}>
            <AgGridReact
                rowData={rowData}
                columnDefs={props.ColumnDefs}
                getRowId={props.GetRowId}
                defaultColDef={defaultColDef}
                onGridReady={props.OnGridReady}
                onSelectionChanged={props.OnSelectionChanged}
                pagination={props.IsPaginationEnabled}
                paginationPageSize={10}
                rowSelection="multiple"
                multiSortKey="ctrl"
                suppressRowClickSelection={true}
                animateRows={true}
                loadingOverlayComponent={loadingOverlayComponent}
                onRowClicked={(e) => !!props.OnGridRowClick && props.OnGridRowClick(e.data)}
                noRowsOverlayComponent={noRowsOverlayComponent}
                noRowsOverlayComponentParams={noRowsOverlayComponentParams}
                gridOptions={gridOptions}
                isExternalFilterPresent={isExternalFilterPresent}
                doesExternalFilterPass={doesExternalFilterPass}
                onCellClicked={(e) => !!props.OnGridCellRowClick && props.OnGridCellRowClick(e, e.data)}
                onColumnResized={props.onColumnResized}
            />
        </div>
    );
};


export default GridComponent;